// src/pages/DispatchPage.js
import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import "./Dispatch.css";
import { useROStore } from "../context/ROContext";
import { useAuth } from "../context/AuthContext";
import { useROUnread } from "../context/ROUnreadContext";
import ROActivityDrawer from "../components/ro/ROActivityDrawer";
import {
  clearLegacyDispatchFilters,
  clearStoredDispatchFilters,
  getDispatchFilterScope,
  getDispatchFilterStorageKey,
  readInitialDispatchFilters,
  writeStoredDispatchFilters,
} from "../utils/dispatchFilterPersistence";
import {
  TRANSPORT_TYPES,
  RO_LIFECYCLE,
  RO_STATUS,
} from "../constants/roStatus";

/* =========================
   Helpers
========================= */
function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeUpper(v) {
  return safeString(v).trim().toUpperCase();
}

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function uniq(values) {
  return Array.from(new Set(asArray(values).filter(Boolean)));
}

function toTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

function formatDueCompact(iso) {
  const t = toTime(iso);
  if (!t) return "—";
  const d = new Date(t);
  return d.toLocaleString([], {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

function pickNestedValue(obj, paths) {
  for (const path of paths || []) {
    const parts = safeString(path).split(".");
    let cur = obj;

    for (const part of parts) {
      cur = cur?.[part];
    }

    if (cur) return cur;
  }

  return "";
}

function pickValue(obj, keys) {
  for (const key of keys || []) {
    const value = obj?.[key];
    if (value) return value;
  }

  return "";
}

function roCreatedISO(ro) {
  return (
    pickNestedValue(ro, [
      "timestamps.createdAt",
      "timestamps.openedAt",
      "timestamps.inAt",
      "timestamps.arrivedAt",
    ]) ||
    pickValue(ro, [
      "createdAtISO",
      "createdAtIso",
      "createdAtUtc",
      "createdAt",
      "openedAtISO",
      "openedAt",
      "inAt",
      "arrivedAt",
    ]) ||
    ""
  );
}

function ageMsFromCreated(ro, asOf = new Date()) {
  const createdTime = toTime(roCreatedISO(ro));
  const asOfTime = asOf instanceof Date ? asOf.getTime() : toTime(asOf);

  if (!Number.isFinite(createdTime) || !Number.isFinite(asOfTime)) {
    return null;
  }

  return Math.max(0, asOfTime - createdTime);
}

function formatAgeFromMs(ms) {
  if (ms == null) return "—";

  const totalMinutes = Math.max(0, Math.round(ms / 60000));

  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (totalHours < 24) {
    return remainingMinutes
      ? `${totalHours}h ${remainingMinutes}m`
      : `${totalHours}h`;
  }

  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;

  return remainingHours ? `${days}d ${remainingHours}h` : `${days}d`;
}

function ageLabel(ro, asOf = new Date()) {
  return formatAgeFromMs(ageMsFromCreated(ro, asOf));
}

function ageDaysNumber(ro, asOf = new Date()) {
  const ms = ageMsFromCreated(ro, asOf);
  if (ms == null) return null;
  return Number((ms / 86400000).toFixed(1));
}

function createdDateLabel(ro) {
  return formatDueCompact(roCreatedISO(ro));
}

function isSameLocalDate(iso, compareTo = new Date()) {
  const t = toTime(iso);
  if (!t) return false;

  const d = new Date(t);

  return (
    d.getFullYear() === compareTo.getFullYear() &&
    d.getMonth() === compareTo.getMonth() &&
    d.getDate() === compareTo.getDate()
  );
}

function minutesUntil(iso) {
  const t = toTime(iso);
  if (!t) return null;
  return Math.round((t - Date.now()) / 60000);
}

function formatRemaining(iso) {
  const mins = minutesUntil(iso);
  if (mins == null) return "—";

  if (mins < 0) {
    const overdue = Math.abs(mins);
    const h = Math.floor(overdue / 60);
    const m = overdue % 60;
    if (h <= 0) return `${m}m overdue`;
    return `${h}h ${m}m overdue`;
  }

  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}m left`;
  return `${h}h ${m}m left`;
}

function getTag(ro) {
  const raw =
    ro?.tagNumber ??
    ro?.tag_number ??
    ro?.tag ??
    ro?.keyTag ??
    ro?.key_tag ??
    ro?.tagId ??
    ro?.tag_id ??
    "";
  return safeString(raw).trim();
}

function getAdvisorName(ro) {
  return safeString(
    ro?.advisorName || ro?.serviceAdvisorName || ro?.writerName || ""
  ).trim();
}

function getTransportLabel(v) {
  const raw = safeUpper(v);
  if (raw === "WAITER") return "Waiter";
  if (raw === "LOANER") return "Loaner";
  if (raw === "SHUTTLE") return "Shuttle";
  return "Other";
}

function latestNote(notes) {
  const raw = safeString(notes).trim();
  if (!raw) return "";

  const blocks = raw
    .split(/\n\s*\n/)
    .map((part) => safeString(part).trim())
    .filter(Boolean);

  if (!blocks.length) return "";

  const latestBlock = blocks[blocks.length - 1];
  const lines = latestBlock
    .split("\n")
    .map((line) => safeString(line).trim())
    .filter(Boolean);

  if (!lines.length) return "";
  if (lines.length === 1) return lines[0];
  return lines.slice(1).join(" ");
}


function getRoleKey(role) {
  return safeString(role)
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function getCurrentUserRoleValues(user) {
  const employments = Array.isArray(user?.employments) ? user.employments : [];

  const candidates = [
    user?.role,
    user?.storeRole,
    user?.store_role,
    user?.jobRole,
    user?.job_role,
    user?.employeeRole,
    user?.employee_role,
    user?.position,
    user?.title,
    user?.roleName,
    user?.role_name,
    user?.membership?.role,
    user?.membership?.storeRole,
    user?.employment?.role,
    user?.employment?.storeRole,
    user?.employment?.store_role,
    user?.employment?.title,
    ...employments.flatMap((employment) => [
      employment?.role,
      employment?.storeRole,
      employment?.store_role,
      employment?.title,
      employment?.position,
    ]),
  ];

  return candidates
    .map((v) => safeString(v).trim())
    .filter(Boolean);
}

function isTechAssignmentRole(role) {
  const r = getRoleKey(role);

  if (!r) return false;

  return (
    r === "manager" ||
    r === "service manager" ||
    r === "service director" ||
    r === "fixed ops manager" ||
    r === "fixed operations manager" ||
    r === "admin" ||
    r === "super admin" ||
    r === "support" ||
    r === "foreman" ||
    r === "shop foreman" ||
    r === "dispatcher" ||
    r === "dispatch" ||
    r.includes("service manager") ||
    r.includes("shop foreman") ||
    r.includes("fixed ops") ||
    r.includes("fixed operations") ||
    r.includes("dispatcher") ||
    r.includes("dispatch")
  );
}

function getCurrentUserRoleValue(user) {
  const values = getCurrentUserRoleValues(user);
  return values.find((v) => isTechAssignmentRole(v)) || values[0] || "";
}

function canAssignTechForRole(role) {
  return isTechAssignmentRole(role);
}

function canAssignTechForUser(user) {
  return getCurrentUserRoleValues(user).some((value) =>
    isTechAssignmentRole(value)
  );
}

function canEditTypeForRole(role) {
  return false;
}

function canEditStatusForRole(role) {
  return false;
}

function canEditFlagForRole(role) {
  // Flag is intentionally editable by anyone in the store.
  // Status remains automatic/read-only because it reflects workflow actions.
  return true;
}

function normalizeLifecycleValue(ro) {
  const s = actionKey(ro?.lifecycle || ro?.lifeCycle || ro?.status);

  if (s === "WAITING_ON_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_ASSIGNMENT") return RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
  if (s === "IN_PROGRESS") return RO_LIFECYCLE.IN_PROGRESS;
  if (s === "COMPLETED") return RO_LIFECYCLE.COMPLETED;
  if (s === "OPEN") return RO_LIFECYCLE.OPEN;
  if (s === "ASSIGNED") return RO_LIFECYCLE.OPEN;
  if (s === "VEHICLE_GONE") return "VEHICLE_GONE";
  if (s === "ARCHIVED") return "ARCHIVED";

  return s || RO_LIFECYCLE.OPEN;
}

function getPartsDisplayKey(ro) {
  const action = actionKey(
    ro?.partsActionStatus ||
      ro?.parts_action_status ||
      ro?.partsAction ||
      ro?.parts_action ||
      ""
  );

  const partsStatus = actionKey(ro?.partsStatus || ro?.parts_status || "");
  const requestType = actionKey(ro?.partsRequestType || ro?.parts_request_type || "");

  const hasActivePartsRequest =
    requestType === "NEED_PARTS" || requestType === "NEED_PRICE";

  const hasReviewAction =
    action === "REVIEW" ||
    action === "OPEN" ||
    action === "REOPEN" ||
    action === "NEEDS_REVIEW" ||
    action === "PARTS_NEEDED" ||
    action === "NEED_PARTS" ||
    action === "NEED_PRICE";

  if (hasActivePartsRequest && hasReviewAction) {
    if (requestType === "NEED_PRICE" || action === "NEED_PRICE") {
      return "PARTS_QUOTE_NEEDED";
    }

    return "PARTS_NEEDED";
  }

  // Prefer the newest concrete parts status over stale action values.
  if (partsStatus === "QUOTED") return "QUOTE_PROVIDED";
  if (partsStatus === "ARRIVED") return "PARTS_ARRIVED";
  if (partsStatus === "ORDERED") return "PARTS_ORDERED";
  if (partsStatus === "PULLED") return "PARTS_PRE_PULLED";

  if (
    action === "QUOTED" ||
    action === "QUOTE_PROVIDED" ||
    action === "PARTS_QUOTED"
  ) {
    return "QUOTE_PROVIDED";
  }

  if (action === "ARRIVED" || action === "PARTS_ARRIVED") {
    return "PARTS_ARRIVED";
  }

  if (action === "ORDERED" || action === "PARTS_ORDERED") {
    return "PARTS_ORDERED";
  }

  if (
    action === "PRE_PULLED" ||
    action === "PREPULLED" ||
    action === "PULLED" ||
    action === "PARTS_PRE_PULLED"
  ) {
    return "PARTS_PRE_PULLED";
  }

  return "";
}

function getBoardStatus(ro) {
  const explicitStatus = actionKey(ro?.status);
  const lifecycle = normalizeLifecycleValue(ro);

  const pickupStatus = actionKey(
    ro?.customerPickupStatus || ro?.customer_pickup_status || ""
  );

  const advisorAction = actionKey(
    ro?.advisorActionStatus || ro?.advisor_action_status || ""
  );

  /*
    Highest priority:
    Vehicle Gone removes the RO from active operational concern.
  */
  if (
    pickupStatus === "VEHICLE_GONE" ||
    advisorAction === "VEHICLE_GONE" ||
    ro?.vehicleGone ||
    ro?.vehicle_gone ||
    ro?.pickedUp ||
    ro?.picked_up ||
    explicitStatus === "VEHICLE_GONE" ||
    lifecycle === "VEHICLE_GONE"
  ) {
    return "VEHICLE_GONE";
  }

  if (explicitStatus === "ARCHIVED" || lifecycle === "ARCHIVED") {
    return "ARCHIVED";
  }

  /*
    Completed work must beat stale parts labels.
    Example: Parts Arrived should not keep showing after technician completes.
  */
  if (lifecycle === RO_LIFECYCLE.COMPLETED || explicitStatus === "COMPLETED") {
    return "REPAIRS_COMPLETE";
  }

  const partsDisplay = getPartsDisplayKey(ro);
  const activePartsRequest =
    partsDisplay === "PARTS_NEEDED" || partsDisplay === "PARTS_QUOTE_NEEDED";

  /*
    Active parts requests still need visibility even if the job was in progress.
    But old actioned states like Pre-Pulled / Arrived / Quoted should not beat live work.
  */
  if (activePartsRequest) return partsDisplay;

  if (lifecycle === RO_LIFECYCLE.IN_PROGRESS || explicitStatus === "IN_PROGRESS") {
    return "IN_PROGRESS";
  }

  if (explicitStatus === "ON_HOLD" || explicitStatus === "VEHICLE_HOLD") {
    return "ON_HOLD";
  }

  if (
    pickupStatus === "CONTACTED_FOR_PICKUP" ||
    advisorAction === "CUSTOMER_CONTACTED" ||
    ro?.customerContacted ||
    ro?.customer_contacted
  ) {
    return "CUSTOMER_CONTACTED";
  }

  /*
    Non-blocking parts states are useful only when the RO is not actively being worked.
  */
  if (partsDisplay) return partsDisplay;

  if (lifecycle === RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT) return "UNASSIGNED";

  if (explicitStatus === "ASSIGNED" || ro?.assignedTechId || ro?.assignedTechName) {
    return "ASSIGNED";
  }

  return "UNASSIGNED";
}

function isWipVehicleGoneLike(ro) {
  const status = safeUpper(ro?.status);
  const lifecycle = safeUpper(ro?.lifecycle || ro?.lifeCycle);

  const pickupStatus = safeUpper(
    ro?.customerPickupStatus || ro?.customer_pickup_status
  );

  const advisorAction = safeUpper(
    ro?.advisorActionStatus || ro?.advisor_action_status
  );

  return (
    status === "VEHICLE_GONE" ||
    status === "PICKED_UP" ||
    lifecycle === "VEHICLE_GONE" ||
    pickupStatus === "VEHICLE_GONE" ||
    advisorAction === "VEHICLE_GONE" ||
    !!ro?.vehicleGone ||
    !!ro?.vehicle_gone ||
    !!ro?.pickedUp ||
    !!ro?.picked_up ||
    !!ro?.pickedUpAt ||
    !!ro?.picked_up_at ||
    !!ro?.pickedUpAtISO ||
    !!ro?.picked_up_at_iso ||
    !!ro?.vehicleGoneAt ||
    !!ro?.vehicle_gone_at ||
    !!ro?.vehicleGoneAtISO ||
    !!ro?.vehicle_gone_at_iso ||
    !!ro?.timestamps?.vehicleGoneAt ||
    !!ro?.timestamps?.vehicle_gone_at ||
    !!ro?.timestamps?.pickedUpAt ||
    !!ro?.timestamps?.picked_up_at
  );
}

function statusDisplay(statusKey) {
  if (statusKey === "UNASSIGNED") return "Unassigned";
  if (statusKey === "ASSIGNED") return "Assigned";
  if (statusKey === "IN_PROGRESS") return "In Progress";
  if (statusKey === "REPAIRS_COMPLETE") return "Repairs Complete";
  if (statusKey === "ON_HOLD") return "On Hold";
  if (statusKey === "VEHICLE_GONE") return "Vehicle Gone";
  if (statusKey === "PARTS_NEEDED") return "Parts Needed";
  if (statusKey === "PARTS_QUOTE_NEEDED") return "Parts Quote Needed";
  if (statusKey === "PARTS_PRE_PULLED") return "Parts Pre-Pulled";
  if (statusKey === "PARTS_ORDERED") return "Parts Ordered";
  if (statusKey === "PARTS_ARRIVED") return "Parts Arrived";
  if (statusKey === "QUOTE_PROVIDED") return "Quote Provided";
  if (statusKey === "CUSTOMER_CONTACTED") return "Contacted for Pick Up";
  return "Unassigned";
}

function getAttentionFlagsFromRO(ro) {
  const direct = ro?.attentionFlags || ro?.attention_flags || ro?.flags || [];
  if (Array.isArray(direct)) {
    return direct
      .map((f) => safeString(f).trim())
      .filter(Boolean);
  }

  const one = safeString(direct).trim();
  return one ? [one] : [];
}

function getPrimaryFlag(ro) {
  const manualText = safeString(ro?.manualFlag).trim();
  const manualTone = safeString(ro?.manualFlagTone).trim().toLowerCase();

  if (manualText) {
    return {
      text: manualText,
      tone: manualTone || "neutral",
      sortValue: 0,
      source: "manual",
    };
  }

  const attentionFlags = getAttentionFlagsFromRO(ro);
  const firstAttention = attentionFlags[0] || "";

  if (firstAttention) {
    const normalized =
      firstAttention.toUpperCase().startsWith("ATTN:")
        ? firstAttention
        : `ATTN: ${firstAttention}`;

    return {
      text: normalized,
      tone: "danger",
      sortValue: 1,
      source: "attention",
    };
  }

  const statusKey = getBoardStatus(ro);
  if (statusKey === "PARTS_NEEDED" || statusKey === "PARTS_QUOTE_NEEDED") {
    return {
      text: statusDisplay(statusKey),
      tone: "orange",
      sortValue: 2,
      source: "parts",
    };
  }

  return { text: "", tone: "neutral", sortValue: 99, source: "none" };
}

function flagPillStyle(tone) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 8px",
    borderRadius: 6,
    fontSize: 10.5,
    fontWeight: 900,
    border: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    lineHeight: 1.05,
    maxWidth: "100%",
  };

  if (tone === "danger") {
    return {
      ...base,
      background: "#fef2f2",
      borderColor: "#fca5a5",
      color: "#991b1b",
    };
  }
  if (tone === "orange") {
    return {
      ...base,
      background: "#fff7ed",
      borderColor: "#fdba74",
      color: "#9a3412",
    };
  }
  if (tone === "blue") {
    return {
      ...base,
      background: "#eff6ff",
      borderColor: "#93c5fd",
      color: "#1d4ed8",
    };
  }
  if (tone === "green") {
    return {
      ...base,
      background: "#ecfdf5",
      borderColor: "#86efac",
      color: "#166534",
    };
  }
  if (tone === "purple") {
    return {
      ...base,
      background: "#f5f3ff",
      borderColor: "#c4b5fd",
      color: "#6d28d9",
    };
  }

  return base;
}

function statusPillStyle(statusKey) {
  let tone = "neutral";

  if (statusKey === "UNASSIGNED") tone = "blue";
  if (statusKey === "IN_PROGRESS") tone = "purple";
  if (statusKey === "ON_HOLD" || statusKey === "PARTS_ORDERED") tone = "orange";
  if (
    statusKey === "REPAIRS_COMPLETE" ||
    statusKey === "PARTS_PRE_PULLED" ||
    statusKey === "PARTS_ARRIVED"
  ) {
    tone = "green";
  }
  if (statusKey === "PARTS_NEEDED" || statusKey === "PARTS_QUOTE_NEEDED") tone = "orange";
  if (statusKey === "QUOTE_PROVIDED") tone = "purple";
  if (statusKey === "CUSTOMER_CONTACTED") tone = "blue";

  return flagPillStyle(tone);
}

function actionKey(v) {
  return safeString(v)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function rowHighlightStyle(flagTone, isOverdue, boardStatus) {
  if (flagTone === "danger" || isOverdue) {
    return { background: "rgba(254, 242, 242, 0.18)" };
  }

  if (boardStatus === "PARTS_NEEDED" || boardStatus === "PARTS_QUOTE_NEEDED" || boardStatus === "PARTS_ORDERED") {
    return { background: "rgba(255, 247, 237, 0.16)" };
  }

  if (boardStatus === "PARTS_PRE_PULLED" || boardStatus === "PARTS_ARRIVED") {
    return { background: "rgba(236, 253, 245, 0.12)" };
  }

  if (boardStatus === "QUOTE_PROVIDED") {
    return { background: "rgba(245, 243, 255, 0.13)" };
  }

  return {};
}

function compareText(a, b) {
  return safeString(a).localeCompare(safeString(b), undefined, {
    sensitivity: "base",
  });
}

function compareNumbers(a, b) {
  return a - b;
}

function commandFieldStyle(active = false) {
  return {
    width: "100%",
    minHeight: 30,
    padding: "5px 9px",
    borderRadius: 6,
    border: active ? "1px solid #94a3b8" : "1px solid #e2e8f0",
    background: active ? "#f8fafc" : "#ffffff",
    color: "#111827",
    fontSize: 11.5,
    fontWeight: 800,
    textAlign: "left",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
}

function filterFieldStyle() {
  return {
    height: 40,
    padding: "0 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#ffffff",
    fontSize: 12.5,
    color: "#111827",
    minWidth: 0,
    width: "100%",
  };
}

function compactHeaderSelectStyle() {
  return {
    height: 28,
    width: "100%",
    minWidth: 0,
    padding: "0 9px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#ffffff",
    fontSize: 11.5,
    fontWeight: 700,
    color: "#111827",
  };
}

function statusFieldStyle(statusKey, active = false) {
  let background = "#ffffff";
  let color = "#0f172a";
  let borderColor = active ? "#94a3b8" : "#e2e8f0";

  if (statusKey === "IN_PROGRESS") {
    background = "#eef2ff";
    color = "#3730a3";
    borderColor = "#c7d2fe";
  } else if (statusKey === "ON_HOLD" || statusKey === "PARTS_ORDERED") {
    background = "#fff7ed";
    color = "#9a3412";
    borderColor = "#fdba74";
  } else if (
    statusKey === "REPAIRS_COMPLETE" ||
    statusKey === "PARTS_PRE_PULLED" ||
    statusKey === "PARTS_ARRIVED"
  ) {
    background = "#ecfdf5";
    color = "#166534";
    borderColor = "#86efac";
  } else if (statusKey === "PARTS_NEEDED" || statusKey === "PARTS_QUOTE_NEEDED") {
    background = "#fff7ed";
    color = "#9a3412";
    borderColor = "#fdba74";
  } else if (statusKey === "QUOTE_PROVIDED") {
    background = "#f5f3ff";
    color = "#6d28d9";
    borderColor = "#c4b5fd";
  } else if (statusKey === "CUSTOMER_CONTACTED") {
    background = "#eff6ff";
    color = "#1d4ed8";
    borderColor = "#93c5fd";
  }

  return {
    ...commandFieldStyle(active),
    background,
    color,
    border: `1px solid ${borderColor}`,
  };
}

function popMenuStyle(width = 170) {
  return {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    zIndex: 9999,
    width,
    background: "#ffffff",
    border: "1px solid #d7dee8",
    borderRadius: 10,
    boxShadow: "0 18px 40px rgba(15,23,42,0.12)",
    padding: 6,
  };
}

function popMenuItemStyle(active = false, destructive = false) {
  return {
    width: "100%",
    textAlign: "left",
    border: "none",
    background: active ? "#f8fafc" : "transparent",
    color: destructive ? "#991b1b" : "#111827",
    borderRadius: 7,
    padding: "8px 10px",
    cursor: "pointer",
    fontSize: 11.5,
    fontWeight: active ? 900 : 700,
  };
}



/* =========================
   Component constants
========================= */
const SORTS = Object.freeze({
  RO_NUMBER: "RO_NUMBER",
  TAG: "TAG",
  ADVISOR: "ADVISOR",
  VEHICLE: "VEHICLE",
  CUSTOMER: "CUSTOMER",
  TYPE: "TYPE",
  DUE: "DUE",
  AGE: "AGE",
  TECHNICIAN: "TECHNICIAN",
  STATUS: "STATUS",
  FLAG: "FLAG",
  NOTES: "NOTES",
  PRIORITY: "PRIORITY",
});

const AGE_SORT_OPTIONS = Object.freeze([
  { value: "AGE_DESC", label: "Oldest first" },
  { value: "AGE_ASC", label: "Newest first" },
]);

const STATUS_OPTIONS = Object.freeze([
  "UNASSIGNED",
  "ASSIGNED",
  "IN_PROGRESS",
  "REPAIRS_COMPLETE",
  "ON_HOLD",
  "VEHICLE_GONE",
  "PARTS_NEEDED",
  "PARTS_QUOTE_NEEDED",
  "PARTS_PRE_PULLED",
  "PARTS_ORDERED",
  "PARTS_ARRIVED",
  "QUOTE_PROVIDED",
  "CUSTOMER_CONTACTED",
]);

const MANUAL_FLAG_OPTIONS = Object.freeze([
  { text: "", tone: "", label: "Clear Manual Override" },
  { text: "ATTN: Advisor", tone: "danger", label: "ATTN: Advisor" },
  { text: "ATTN: Dispatch", tone: "danger", label: "ATTN: Dispatch" },
  { text: "ATTN: Parts", tone: "danger", label: "ATTN: Parts" },
  { text: "ATTN: Service Manager", tone: "danger", label: "ATTN: Service Manager" },
  { text: "ATTN: Shop Foreman", tone: "danger", label: "ATTN: Shop Foreman" },
  { text: "Need Quote", tone: "orange", label: "Need Quote" },
  { text: "Quote Provided", tone: "purple", label: "Quote Provided" },
  { text: "Parts Ordered", tone: "orange", label: "Parts Ordered" },
  { text: "Parts Pre-Pulled", tone: "green", label: "Parts Pre-Pulled" },
  { text: "Parts Arrived", tone: "green", label: "Parts Arrived" },
]);

const COL = Object.freeze({
  RO: "5.5%",
  TAG: "5%",
  ADVISOR: "8.5%",
  VEHICLE: "15.5%",
  TYPE: "6.5%",
  DUE: "9%",
  AGE: "9%",
  PRIORITY: "7%",
  TECHNICIAN: "10.5%",
  STATUS: "10%",
  FLAG: "8.5%",
  NOTES: "9.5%",
});

const PRO_HEADER_CELL_STYLE = Object.freeze({
  position: "sticky",
  top: 0,
  zIndex: 35,
  textAlign: "left",
  padding: "8px 6px",
  fontSize: 10,
  fontWeight: 800,
  color: "#6b7280",
  background: "#f8fafc",
  borderTop: "1px solid #e5e7eb",
  borderBottom: "1px solid #dbe3ee",
  boxShadow: "0 1px 0 rgba(15,23,42,0.04)",
});

function getPriorityAnchorISO(ro) {
  const lifecycle = normalizeLifecycleValue(ro);

  if (lifecycle === RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT) {
    return (
      ro?.timestamps?.waitingAssignmentAt ||
      ro?.waitingAssignmentAt ||
      ro?.createdAt ||
      ro?.timestamps?.createdAt ||
      ""
    );
  }

  if (lifecycle === RO_LIFECYCLE.WAITING_FOR_PARTS) {
    return (
      ro?.timestamps?.waitingPartsAt ||
      ro?.waitingPartsAt ||
      ro?.waiting_parts_at ||
      ro?.createdAt ||
      ro?.timestamps?.createdAt ||
      ""
    );
  }

  if (lifecycle === RO_LIFECYCLE.IN_PROGRESS) {
    return (
      ro?.timestamps?.workStartAt ||
      ro?.workStartAt ||
      ro?.startedAt ||
      ro?.createdAt ||
      ro?.timestamps?.createdAt ||
      ""
    );
  }

  return ro?.createdAt || ro?.timestamps?.createdAt || "";
}

function getPriorityScore(ro) {
  const boardStatus = getBoardStatus(ro);
  const transport = safeUpper(ro?.transportType);
  const promiseISO = ro?.promiseTimeISO || ro?.promiseTime || ro?.promise_time_iso;

  const minsToPromise = minutesUntil(promiseISO);

  const anchorISO = getPriorityAnchorISO(ro);
  const anchorTime = toTime(anchorISO);
  const minsAging = anchorTime
    ? Math.max(0, Math.round((Date.now() - anchorTime) / 60000))
    : 0;

  let base = 10;

  if (minsToPromise != null && minsToPromise < 0) base = 100;
  else if (minsToPromise != null && minsToPromise <= 60) base = 90;
  else if (transport === TRANSPORT_TYPES.WAITER) base = 80;
  else if (boardStatus === "UNASSIGNED") base = 70;
  else if (boardStatus === "PARTS_NEEDED" || boardStatus === "PARTS_QUOTE_NEEDED") base = 66;
  else if (boardStatus === "IN_PROGRESS") base = 50;
  else if (boardStatus === "ON_HOLD") base = 40;
  else if (boardStatus === "ASSIGNED") base = 30;

  const agingBoost = Math.min(20, Math.floor(minsAging / 30));

  return base + agingBoost;
}

function getPrioritySignal(ro) {
  const score = getPriorityScore(ro);
  const promiseISO = ro?.promiseTimeISO || ro?.promiseTime || ro?.promise_time_iso;
  const minsToPromise = minutesUntil(promiseISO);
  const boardStatus = getBoardStatus(ro);
  const transport = safeUpper(ro?.transportType);

  if (minsToPromise != null && minsToPromise < 0) {
    return { label: "Overdue", tone: "danger", score };
  }

  if (minsToPromise != null && minsToPromise <= 60) {
    return { label: "Due Soon", tone: "orange", score };
  }

  if (transport === TRANSPORT_TYPES.WAITER) {
    return { label: "Waiter", tone: "danger", score };
  }

  if (boardStatus === "PARTS_NEEDED" || boardStatus === "PARTS_QUOTE_NEEDED") {
    return { label: statusDisplay(boardStatus), tone: "orange", score };
  }

  if (boardStatus === "UNASSIGNED") {
    return { label: "Unassigned", tone: "blue", score };
  }

  if (boardStatus === "IN_PROGRESS") {
    return { label: "In Progress", tone: "purple", score };
  }

  return { label: "Normal", tone: "neutral", score };
}

export default function DispatchPage() {
  const store = useROStore();
  const auth = useAuth();
  const {
    hasUnread,
    isHydrated: unreadHydrated,
    storeId: unreadStoreId,
  } = useROUnread();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterScope = useMemo(() => getDispatchFilterScope(auth), [auth]);
  const filterStorageKey = useMemo(
    () => getDispatchFilterStorageKey(filterScope),
    [filterScope]
  );
  const initialFilters = useMemo(
    () => readInitialDispatchFilters(searchParams, filterStorageKey),
    [searchParams, filterStorageKey]
  );

  const repairOrders = store?.repairOrders;
  const rosRaw = store?.ros;
  const visibleWipRaw = store?.visibleWipROs;
  const techsRaw = store?.techs;

  const assignTech = store?.assignTech;
  const unassignTech = store?.unassignTech;
  const updateRO = store?.updateRO;
  const updateStatus = store?.updateStatus;
  const setTransportType = store?.setTransportType;
  const requestParts = store?.requestParts;
  const markVehicleGone = store?.markVehicleGone;

const currentUser = store?.currentUser || {};
const actorRole = getCurrentUserRoleValue(currentUser) || "user";
const actorName = safeString(currentUser?.name || "User");

const canAssignTech =
  canAssignTechForUser(currentUser) || canAssignTechForRole(actorRole);
  const canEditType = canEditTypeForRole(actorRole);
  const canEditStatus = canEditStatusForRole(actorRole);
  const canEditFlag = canEditFlagForRole(actorRole);

  const [search, setSearch] = useState(initialFilters.search);
  const [sortBy, setSortBy] = useState(SORTS.AGE);
  const [sortDir, setSortDir] = useState("asc");
  const [flashByRO, setFlashByRO] = useState({});
  const [editor, setEditor] = useState(null);
  const [includePickedUpVehicles, setIncludePickedUpVehicles] = useState(
    initialFilters.includePickedUpVehicles
  );

  const [advisorFilter, setAdvisorFilter] = useState(initialFilters.advisorFilter);
  const [technicianFilter, setTechnicianFilter] = useState(initialFilters.technicianFilter);
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter);
  const [flagFilter, setFlagFilter] = useState(initialFilters.flagFilter);
  const [typeFilter, setTypeFilter] = useState(initialFilters.typeFilter);
  const [overdueOnly, setOverdueOnly] = useState(initialFilters.overdueOnly);
  const [filterHydrationKey, setFilterHydrationKey] = useState(filterStorageKey);

  const isFiltered =
    safeString(search).trim() ||
    safeString(advisorFilter).trim() ||
    safeString(technicianFilter).trim() ||
    safeString(statusFilter).trim() ||
    safeString(flagFilter).trim() ||
    safeString(typeFilter).trim() ||
    overdueOnly ||
    includePickedUpVehicles;

  const [activityROId, setActivityROId] = useState(null);
  const [flagModalROId, setFlagModalROId] = useState(null);
  const currentUnreadStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId
  ).trim();
  const unreadStoreMatches =
    !!currentUnreadStoreId &&
    safeString(unreadStoreId).trim() === currentUnreadStoreId;

  const flashTimeoutsRef = useRef({});
  const popoverRootRef = useRef(null);
  const hasAppliedScopedFiltersRef = useRef(!!filterStorageKey);

  const tagParam = safeString(searchParams.get("tag")).trim();

  useEffect(() => {
    const timeouts = flashTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, []);

  useEffect(() => {
    clearLegacyDispatchFilters();
  }, []);

  useEffect(() => {
    if (!tagParam) return;
    setSearch((prev) => {
      const current = safeString(prev).trim();
      return current.toLowerCase() === tagParam.toLowerCase() ? prev : tagParam;
    });
  }, [tagParam]);

  useEffect(() => {
    function onDocMouseDown(e) {
      if (!popoverRootRef.current) return;
      if (!popoverRootRef.current.contains(e.target)) {
        setEditor(null);
      }
    }

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    if (filterHydrationKey === filterStorageKey) return;

    const shouldUseUrlFilters =
      !hasAppliedScopedFiltersRef.current && !!filterStorageKey;
    const nextFilters = readInitialDispatchFilters(
      shouldUseUrlFilters ? searchParams : new URLSearchParams(),
      filterStorageKey
    );

    if (filterStorageKey) {
      hasAppliedScopedFiltersRef.current = true;
    }

    setSearch(nextFilters.search);
    setAdvisorFilter(nextFilters.advisorFilter);
    setTechnicianFilter(nextFilters.technicianFilter);
    setStatusFilter(nextFilters.statusFilter);
    setFlagFilter(nextFilters.flagFilter);
    setTypeFilter(nextFilters.typeFilter);
    setOverdueOnly(nextFilters.overdueOnly);
    setIncludePickedUpVehicles(nextFilters.includePickedUpVehicles);
    setFilterHydrationKey(filterStorageKey);
  }, [filterHydrationKey, filterStorageKey, searchParams]);

  useEffect(() => {
    if (filterHydrationKey !== filterStorageKey) return;

    const payload = {
      search: safeString(search).trim(),
      advisor: safeString(advisorFilter).trim(),
      tech: safeString(technicianFilter).trim(),
      status: safeString(statusFilter).trim(),
      flag: safeString(flagFilter).trim(),
      type: safeString(typeFilter).trim(),
      overdue: overdueOnly ? "1" : "0",
      pickedUp: includePickedUpVehicles ? "1" : "0",
    };

    writeStoredDispatchFilters(filterStorageKey, {
      search: payload.search,
      advisor: payload.advisor,
      tech: payload.tech,
      status: payload.status,
      flag: payload.flag,
      type: payload.type,
      overdue: overdueOnly,
      pickedUp: includePickedUpVehicles,
    });

    const next = new URLSearchParams(searchParams);

    const setOrDelete = (key, value, emptyValue = "") => {
      if (value === emptyValue) next.delete(key);
      else next.set(key, value);
    };

    setOrDelete("search", payload.search);
    setOrDelete("advisor", payload.advisor);
    setOrDelete("tech", payload.tech);
    setOrDelete("status", payload.status);
    setOrDelete("flag", payload.flag);
    setOrDelete("type", payload.type);
    next.delete("age");
    setOrDelete("overdue", payload.overdue, "0");
    setOrDelete("pickedUp", payload.pickedUp, "0");

    const currentString = searchParams.toString();
    const nextString = next.toString();
    if (currentString !== nextString) {
      setSearchParams(next, { replace: true });
    }
  }, [
    search,
    advisorFilter,
    technicianFilter,
    statusFilter,
    flagFilter,
    typeFilter,
    overdueOnly,
    includePickedUpVehicles,
    filterHydrationKey,
    filterStorageKey,
    searchParams,
    setSearchParams,
  ]);

  const clearTagParam = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("tag");
    setSearchParams(next, { replace: true });
    setSearch("");
  }, [searchParams, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    clearStoredDispatchFilters(filterStorageKey);
    setSearch("");
    setAdvisorFilter("");
    setTechnicianFilter("");
    setStatusFilter("");
    setFlagFilter("");
    setTypeFilter("");
    setOverdueOnly(false);
    setIncludePickedUpVehicles(false);
  }, [filterStorageKey]);

  const flash = useCallback((roId, msg) => {
    if (!roId) return;

    if (flashTimeoutsRef.current[roId]) {
      window.clearTimeout(flashTimeoutsRef.current[roId]);
    }

    setFlashByRO((m) => ({ ...m, [roId]: msg }));

    flashTimeoutsRef.current[roId] = window.setTimeout(() => {
      setFlashByRO((m) => {
        const next = { ...m };
        delete next[roId];
        return next;
      });
      delete flashTimeoutsRef.current[roId];
    }, 1600);
  }, []);

  const allROs = useMemo(() => {
    const a = Array.isArray(repairOrders) ? repairOrders : null;
    const b = Array.isArray(rosRaw) ? rosRaw : null;
    return a || b || [];
  }, [repairOrders, rosRaw]);

  const visibleWipROs = useMemo(() => {
    const fromContext = Array.isArray(visibleWipRaw) ? visibleWipRaw : [];
    const base = fromContext.length ? fromContext : allROs;

    const byId = new Map();

    base.forEach((ro) => {
      if (!ro?.id) return;
      byId.set(ro.id, ro);
    });

    /*
      Keep same-day completed / vehicle-gone ROs visible on WIP.
      This supports the real shop workflow:
      - Tech completes work -> Repairs Complete remains visible.
      - Advisor marks Vehicle Gone -> Vehicle Gone remains visible for the day.
      - History/search still keeps the permanent record.
    */
    allROs.forEach((ro) => {
      if (!ro?.id) return;

      const boardStatus = getBoardStatus(ro);

      if (boardStatus !== "REPAIRS_COMPLETE" && boardStatus !== "VEHICLE_GONE") {
        return;
      }

      const actionISO =
        ro?.completedAtISO ||
        ro?.technicianCompletedAtISO ||
        ro?.vehicleGoneAtISO ||
        ro?.pickedUpAtISO ||
        ro?.vehicleGoneAt ||
        ro?.pickedUpAt ||
        ro?.timestamps?.completedAt ||
        ro?.timestamps?.technicianCompletedAt ||
        ro?.timestamps?.vehicleGoneAt ||
        ro?.timestamps?.pickedUpAt ||
        ro?.timestamps?.updatedAt ||
        ro?.updatedAt ||
        "";

      if (isSameLocalDate(actionISO)) {
        byId.set(ro.id, ro);
      }
    });

    return Array.from(byId.values());
  }, [visibleWipRaw, allROs]);

  const techs = useMemo(() => asArray(techsRaw), [techsRaw]);

  const techNameById = useMemo(() => {
    const map = new Map();
    techs.forEach((t) => {
      map.set(String(t.id), safeString(t.name).trim() || String(t.id));
    });
    return map;
  }, [techs]);

  const getTechName = useCallback(
    (techId) => {
      if (!techId) return "";
      return techNameById.get(String(techId)) || String(techId);
    },
    [techNameById]
  );

  const activeWip = useMemo(() => {
    return visibleWipROs.filter((ro) => {
      const st = safeUpper(ro?.status);
      const lc = safeUpper(ro?.lifecycle);
      return st !== safeUpper(RO_STATUS.ARCHIVED) && lc !== "ARCHIVED";
    });
  }, [visibleWipROs]);

  const boardRows = useMemo(() => {
    return activeWip.filter((ro) => {
      const boardStatus = getBoardStatus(ro);

      if (boardStatus !== "VEHICLE_GONE") return true;

      /*
        Vehicle Gone should stay visible on today's WIP by default.
        After today, it only appears when Show Vehicle Gone is checked.
      */
      const goneAt =
        ro?.vehicleGoneAtISO ||
        ro?.pickedUpAtISO ||
        ro?.vehicleGoneAt ||
        ro?.pickedUpAt ||
        ro?.timestamps?.vehicleGoneAt ||
        ro?.timestamps?.pickedUpAt ||
        ro?.timestamps?.updatedAt ||
        ro?.updatedAt ||
        "";

      if (isSameLocalDate(goneAt)) return true;

      return includePickedUpVehicles;
    });
  }, [activeWip, includePickedUpVehicles]);

  const advisorOptions = useMemo(() => {
    return uniq(boardRows.map((ro) => getAdvisorName(ro))).sort((a, b) =>
      compareText(a, b)
    );
  }, [boardRows]);

  const technicianOptions = useMemo(() => {
    const fromRows = boardRows.map((ro) =>
      safeString(ro?.assignedTechName).trim() || getTechName(ro?.assignedTechId)
    );
    const fromDirectory = techs.map((t) => safeString(t?.name).trim());
    return uniq([...fromRows, ...fromDirectory].filter(Boolean)).sort((a, b) =>
      compareText(a, b)
    );
  }, [boardRows, techs, getTechName]);

  const flagOptions = useMemo(() => {
    const manualFlags = boardRows
      .map((ro) => getPrimaryFlag(ro).text)
      .filter(Boolean);
    const predefined = MANUAL_FLAG_OPTIONS.map((opt) => opt.text).filter(Boolean);
    return uniq([...predefined, ...manualFlags]).sort((a, b) => compareText(a, b));
  }, [boardRows]);

  const typeOptions = useMemo(() => {
    return uniq(boardRows.map((ro) => getTransportLabel(ro?.transportType))).sort(
      (a, b) => compareText(a, b)
    );
  }, [boardRows]);

  const filteredRows = useMemo(() => {
    const q = safeString(search).trim().toLowerCase();

    return boardRows.filter((ro) => {
      if (!includePickedUpVehicles && isWipVehicleGoneLike(ro)) return false;

      const primaryFlag = getPrimaryFlag(ro);
      const advisorName = getAdvisorName(ro);
      const technicianName =
        safeString(ro?.assignedTechName).trim() || getTechName(ro?.assignedTechId);
      const boardStatus = getBoardStatus(ro);
      const typeLabel = getTransportLabel(ro?.transportType);
      const dueISO = ro?.promiseTimeISO || ro?.promiseTime;
      const dueMins = minutesUntil(dueISO);
      const isOverdue = dueMins != null && dueMins < 0;

      if (advisorFilter && advisorName !== advisorFilter) return false;
      if (technicianFilter && technicianName !== technicianFilter) return false;
      if (statusFilter && boardStatus !== statusFilter) return false;
      if (flagFilter && safeString(primaryFlag.text).trim() !== flagFilter) return false;
      if (typeFilter && typeLabel !== typeFilter) return false;
      if (overdueOnly && !isOverdue) return false;

      if (!q) return true;

      return (
        safeString(ro?.roNumber).toLowerCase().includes(q) ||
        getTag(ro).toLowerCase().includes(q) ||
        advisorName.toLowerCase().includes(q) ||
        safeString(ro?.vehicle).toLowerCase().includes(q) ||
        safeString(ro?.customerName).toLowerCase().includes(q) ||
        typeLabel.toLowerCase().includes(q) ||
        formatDueCompact(dueISO).toLowerCase().includes(q) ||
        createdDateLabel(ro).toLowerCase().includes(q) ||
        ageLabel(ro).toLowerCase().includes(q) ||
        technicianName.toLowerCase().includes(q) ||
        statusDisplay(boardStatus).toLowerCase().includes(q) ||
        safeString(primaryFlag.text).toLowerCase().includes(q) ||
        safeString(ro?.notes).toLowerCase().includes(q)
      );
    });
  }, [
    boardRows,
    search,
    advisorFilter,
    technicianFilter,
    statusFilter,
    flagFilter,
    typeFilter,
    overdueOnly,
    includePickedUpVehicles,
    getTechName,
  ]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];

    copy.sort((a, b) => {
      const aFlag = getPrimaryFlag(a);
      const bFlag = getPrimaryFlag(b);

      let result = 0;

      switch (sortBy) {
        case SORTS.RO_NUMBER:
          result = compareNumbers(
            Number(safeString(a?.roNumber || "").replace(/\D/g, "") || 0),
            Number(safeString(b?.roNumber || "").replace(/\D/g, "") || 0)
          );
          break;

        case SORTS.TAG:
          result = compareText(getTag(a), getTag(b));
          break;

        case SORTS.ADVISOR:
          result = compareText(getAdvisorName(a), getAdvisorName(b));
          break;

        case SORTS.VEHICLE:
          result = compareText(a?.vehicle, b?.vehicle);
          break;

        case SORTS.CUSTOMER:
          result = compareText(a?.customerName, b?.customerName);
          break;

        case SORTS.TYPE:
          result = compareText(
            getTransportLabel(a?.transportType),
            getTransportLabel(b?.transportType)
          );
          break;

        case SORTS.DUE: {
          const at = toTime(a?.promiseTimeISO || a?.promiseTime);
          const bt = toTime(b?.promiseTimeISO || b?.promiseTime);
          result = compareNumbers(
            at == null ? Number.POSITIVE_INFINITY : at,
            bt == null ? Number.POSITIVE_INFINITY : bt
          );
          break;
        }

        case SORTS.AGE: {
          const at = toTime(roCreatedISO(a)) || 0;
          const bt = toTime(roCreatedISO(b)) || 0;

          // Base direction is newest first so AGE_ASC matches the
          // existing "Newest first" option exactly by created timestamp.
          result = compareNumbers(bt, at);
          break;
        }

        case SORTS.TECHNICIAN:
          result = compareText(
            safeString(a?.assignedTechName || getTechName(a?.assignedTechId)),
            safeString(b?.assignedTechName || getTechName(b?.assignedTechId))
          );
          break;

        case SORTS.STATUS:
          result = compareText(getBoardStatus(a), getBoardStatus(b));
          break;

        case SORTS.FLAG:
          result = compareText(aFlag.text, bFlag.text);
          break;

        case SORTS.NOTES:
          result = compareText(latestNote(a?.notes), latestNote(b?.notes));
          break;

        case SORTS.PRIORITY: {
          result = getPriorityScore(b) - getPriorityScore(a);

          if (result === 0) {
            const ap = toTime(a?.promiseTimeISO || a?.promiseTime) || Number.POSITIVE_INFINITY;
            const bp = toTime(b?.promiseTimeISO || b?.promiseTime) || Number.POSITIVE_INFINITY;
            result = ap - bp;
          }

          break;
        }

        default:
          result = 0;
      }

      if (result === 0) {
        const at = toTime(roCreatedISO(a)) || 0;
        const bt = toTime(roCreatedISO(b)) || 0;
        result = bt - at;
      }

      if (sortBy === SORTS.PRIORITY) return result;
      return sortDir === "asc" ? result : result * -1;
    });

    return copy;
  }, [filteredRows, sortBy, sortDir, getTechName]);

  const summary = useMemo(() => {
    let totalVisible = 0;
    let unassigned = 0;
    let waiters = 0;
    let partsReview = 0;
    let inProgress = 0;
    let onHold = 0;
    let pickedUp = 0;
    let overdue = 0;

    boardRows.forEach((ro) => {
      totalVisible += 1;

      const boardStatus = getBoardStatus(ro);
      const isVehicleGone = boardStatus === "VEHICLE_GONE";

      if (isVehicleGone) {
        pickedUp += 1;
        return;
      }

      if (boardStatus === "UNASSIGNED") unassigned += 1;
      if (boardStatus === "PARTS_NEEDED" || boardStatus === "PARTS_QUOTE_NEEDED") partsReview += 1;
      if (safeUpper(ro?.transportType) === safeUpper(TRANSPORT_TYPES.WAITER)) {
        waiters += 1;
      }
      if (boardStatus === "IN_PROGRESS") inProgress += 1;
      if (boardStatus === "ON_HOLD") onHold += 1;

      const mins = minutesUntil(ro?.promiseTimeISO || ro?.promiseTime);
      if (mins != null && mins < 0) overdue += 1;
    });

    return {
      totalVisible,
      unassigned,
      waiters,
      partsReview,
      inProgress,
      onHold,
      pickedUp,
      overdue,
    };
  }, [boardRows]);

  const techOptions = useMemo(() => {
    return techs.map((t) => ({
      value: String(t.id),
      label: safeString(t.name),
    }));
  }, [techs]);

  const activityRO = useMemo(() => {
    if (!activityROId) return null;
    return sortedRows.find((ro) => ro?.id === activityROId) || null;
  }, [activityROId, sortedRows]);

  const flagModalRO = useMemo(() => {
    if (!flagModalROId) return null;

    return (
      sortedRows.find((ro) => String(ro?.id) === String(flagModalROId)) ||
      boardRows.find((ro) => String(ro?.id) === String(flagModalROId)) ||
      allROs.find((ro) => String(ro?.id) === String(flagModalROId)) ||
      null
    );
  }, [flagModalROId, sortedRows, boardRows, allROs]);

  const attentionItems = useMemo(() => {
    const byCreatedOldest = (a, b) => {
      const at = toTime(roCreatedISO(a)) || 0;
      const bt = toTime(roCreatedISO(b)) || 0;
      return at - bt;
    };

    const sourceRows = filteredRows.filter(
      (ro) => getBoardStatus(ro) !== "VEHICLE_GONE"
    );

    const overdueRows = sourceRows
      .filter((ro) => {
        const mins = minutesUntil(ro?.promiseTimeISO || ro?.promiseTime);
        return mins != null && mins < 0;
      })
      .sort((a, b) => {
        const at = toTime(a?.promiseTimeISO || a?.promiseTime) || 0;
        const bt = toTime(b?.promiseTimeISO || b?.promiseTime) || 0;
        return at - bt;
      });

    const oldestUnassigned = sourceRows
      .filter((ro) => getBoardStatus(ro) === "UNASSIGNED")
      .sort(byCreatedOldest)[0];

    const oldestPartsReview = sourceRows
      .filter((ro) => {
        const s = getBoardStatus(ro);
        return s === "PARTS_NEEDED" || s === "PARTS_QUOTE_NEEDED";
      })
      .sort(byCreatedOldest)[0];

    const oldestWaiter = sourceRows
      .filter(
        (ro) => safeUpper(ro?.transportType) === safeUpper(TRANSPORT_TYPES.WAITER)
      )
      .sort(byCreatedOldest)[0];

    const mostOverdue = overdueRows[0];
    const oldestInShop = [...sourceRows].sort(byCreatedOldest)[0];

    return [
      oldestInShop
        ? {
            label: "Oldest In Shop",
            ro: oldestInShop,
            detail: `Age: ${ageLabel(oldestInShop)} • Created ${createdDateLabel(oldestInShop)}`,
            tone: "danger",
          }
        : null,
      oldestUnassigned
        ? {
            label: "Oldest Unassigned",
            ro: oldestUnassigned,
            detail: "Needs assignment",
            tone: "danger",
          }
        : null,
      mostOverdue
        ? {
            label: "Most Overdue",
            ro: mostOverdue,
            detail: formatRemaining(mostOverdue?.promiseTimeISO || mostOverdue?.promiseTime),
            tone: "danger",
          }
        : null,
      oldestPartsReview
        ? {
            label: "Parts Review",
            ro: oldestPartsReview,
            detail: "Needs parts action",
            tone: "warn",
          }
        : null,
      oldestWaiter
        ? {
            label: "Oldest Waiter",
            ro: oldestWaiter,
            detail: "Customer waiting",
            tone: "warn",
          }
        : null,
    ].filter(Boolean);
  }, [filteredRows]);

  const hasUnreadNote = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      return !!(unreadHydrated && unreadStoreMatches && roId && hasUnread(roId));
    },
    [hasUnread, unreadHydrated, unreadStoreMatches]
  );

  const openActivity = useCallback(
    (ro) => {
      if (!ro?.id) return;
      setActivityROId(ro.id);
    },
    []
  );

  const closeActivity = useCallback(() => {
    setActivityROId(null);
  }, []);

  const toggleSort = useCallback((nextKey) => {
    setSortBy((prev) => {
      if (prev === nextKey) {
        setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
        return prev;
      }

      setSortDir(nextKey === SORTS.AGE ? "desc" : "asc");
      return nextKey;
    });
  }, []);

  const openEditor = useCallback((roId, field) => {
    setEditor((prev) => {
      if (prev?.roId === roId && prev?.field === field) return null;
      return { roId, field };
    });
  }, []);

  const closeEditor = useCallback(() => {
    setEditor(null);
  }, []);

  const doAssignChange = useCallback(
    (ro, nextTechId) => {
      const roId = ro?.id;
      if (!roId || !canAssignTech) return;

      const cleanNext = safeString(nextTechId).trim();
      const currentTechId = safeString(ro?.assignedTechId).trim();

      if (cleanNext === currentTechId) {
        closeEditor();
        return;
      }

      if (!cleanNext) {
        if (typeof unassignTech !== "function") {
          alert("Missing unassignTech in ROContext.");
          return;
        }
        unassignTech(roId);
        flash(roId, "Moved back to unassigned");
        closeEditor();
        return;
      }

      const tech = techs.find((t) => String(t.id) === String(cleanNext));
      if (!tech) {
        alert("Technician not found.");
        return;
      }

      if (typeof assignTech !== "function") {
        alert("Missing assignTech in ROContext.");
        return;
      }

      assignTech(roId, tech.id, tech.name);
      flash(roId, `Assigned to ${tech.name}`);
      closeEditor();
    },
    [assignTech, unassignTech, techs, flash, canAssignTech, closeEditor]
  );

  const doChangeTransport = useCallback(
    (ro, nextTransport) => {
      if (!ro?.id || !canEditType) return;

      if (typeof setTransportType === "function") {
        setTransportType(ro.id, nextTransport);
      } else if (typeof updateRO === "function") {
        updateRO(
          ro.id,
          {
            transportType: nextTransport,
            timestamps: {
              ...(ro?.timestamps || {}),
              updatedAt: new Date().toISOString(),
            },
          },
          "RO_TRANSPORT_CHANGED",
          { to: nextTransport }
        );
      } else {
        alert("Missing transport update function in ROContext.");
        return;
      }

      flash(ro.id, "Type updated");
      closeEditor();
    },
    [setTransportType, updateRO, flash, canEditType, closeEditor]
  );

  const doChangeStatus = useCallback(
    (ro, nextBoardStatus) => {
      if (!ro?.id || !canEditStatus) return;

      if (nextBoardStatus === "VEHICLE_GONE" && typeof markVehicleGone === "function") {
        markVehicleGone(ro.id, { source: "wip_board" });
        flash(ro.id, "Vehicle marked gone");
        closeEditor();
        return;
      }

      if (
        (nextBoardStatus === "PARTS_NEEDED" || nextBoardStatus === "PARTS_QUOTE_NEEDED") &&
        typeof requestParts === "function"
      ) {
        requestParts(ro.id, nextBoardStatus === "PARTS_QUOTE_NEEDED" ? "NEED_PRICE" : "NEED_PARTS");
        flash(ro.id, "Sent to Parts review");
        closeEditor();
        return;
      }

      if (typeof updateStatus === "function") {
        if (nextBoardStatus === "UNASSIGNED") updateStatus(ro.id, "AVAILABLE", { source: "wip_board" });
        else if (nextBoardStatus === "ASSIGNED") updateStatus(ro.id, "ASSIGNED", { source: "wip_board" });
        else if (nextBoardStatus === "IN_PROGRESS") updateStatus(ro.id, "IN_PROGRESS", { source: "wip_board" });
        else if (nextBoardStatus === "REPAIRS_COMPLETE") updateStatus(ro.id, "COMPLETED", { source: "wip_board" });
        else if (nextBoardStatus === "ON_HOLD") updateStatus(ro.id, "ON_HOLD", { source: "wip_board" });
        else updateStatus(ro.id, nextBoardStatus, { source: "wip_board" });

        flash(ro.id, "Status updated");
        closeEditor();
        return;
      }

      if (typeof updateRO !== "function") {
        alert("Missing updateRO in ROContext.");
        return;
      }

      if (nextBoardStatus === "UNASSIGNED") {
        updateRO(
          ro.id,
          {
            status: RO_STATUS.AVAILABLE,
            lifecycle: RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT,
            assignedTechId: "",
            assignedTechName: "",
          },
          "RO_STATUS_CHANGED",
          { to: "UNASSIGNED" }
        );
      } else if (nextBoardStatus === "ASSIGNED") {
        updateRO(
          ro.id,
          {
            status: RO_STATUS.ASSIGNED,
            lifecycle: RO_LIFECYCLE.OPEN,
          },
          "RO_STATUS_CHANGED",
          { to: "ASSIGNED" }
        );
      } else if (nextBoardStatus === "IN_PROGRESS") {
        updateRO(
          ro.id,
          {
            status: RO_STATUS.IN_PROGRESS,
            lifecycle: RO_LIFECYCLE.IN_PROGRESS,
          },
          "RO_STATUS_CHANGED",
          { to: "IN_PROGRESS" }
        );
      } else if (nextBoardStatus === "REPAIRS_COMPLETE") {
        updateRO(
          ro.id,
          {
            status: RO_STATUS.COMPLETED,
            lifecycle: RO_LIFECYCLE.COMPLETED,
            completedAtISO:
              safeString(ro?.completedAtISO).trim() || new Date().toISOString(),
            timestamps: {
              ...(ro?.timestamps || {}),
              completedAt:
                safeString(ro?.timestamps?.completedAt).trim() ||
                new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          },
          "RO_STATUS_CHANGED",
          { to: "REPAIRS_COMPLETE" }
        );
      } else if (nextBoardStatus === "ON_HOLD") {
        updateRO(
          ro.id,
          {
            status: "ON_HOLD",
            lifecycle: RO_LIFECYCLE.OPEN,
          },
          "RO_STATUS_CHANGED",
          { to: "ON_HOLD" }
        );
      }

      flash(ro.id, "Status updated");
      closeEditor();
    },
    [
      updateRO,
      updateStatus,
      requestParts,
      markVehicleGone,
      flash,
      canEditStatus,
      closeEditor,
    ]
  );

  const doChangeFlag = useCallback(
    async (ro, nextFlagText, nextFlagTone) => {
      if (!ro?.id || !canEditFlag) return;

      if (typeof updateRO !== "function") {
        alert("Missing updateRO in ROContext.");
        return;
      }

      const cleanText = safeString(nextFlagText).trim();
      const cleanTone = safeString(nextFlagTone).trim() || (cleanText ? "danger" : "");

      /*
        Manual flag edits are an override only.

        They intentionally do not change Status, lifecycle, parts request state,
        or automatic attention flags. That preserves the workflow logic where
        Status/automatic Flag are driven by role actions like tech requests,
        parts actions, advisor pickup contact, and vehicle-gone cleanup.

        Clearing here clears the manual override so the automatic flag, if any,
        can show again.
      */
      await Promise.resolve(
        updateRO(
          ro.id,
          {
            manualFlag: cleanText,
            manualFlagTone: cleanTone,
            manualFlagUpdatedAtISO: new Date().toISOString(),
            manualFlagUpdatedByRole: actorRole,
            manualFlagUpdatedByName: actorName,
            timestamps: {
              ...(ro?.timestamps || {}),
              updatedAt: new Date().toISOString(),
            },
          },
          cleanText ? "RO_MANUAL_FLAG_SET" : "RO_MANUAL_FLAG_CLEARED",
          { text: cleanText, tone: cleanTone, actorRole, actorName }
        )
      );

      flash(ro.id, cleanText ? "Flag updated" : "Manual flag cleared");
      setFlagModalROId(null);
      closeEditor();
    },
    [updateRO, actorRole, actorName, flash, canEditFlag, closeEditor]
  );

  const renderSortLabel = (key, label) => {
    const active = sortBy === key;
    const arrow = !active
      ? ""
      : key === SORTS.PRIORITY
      ? " ▼"
      : sortDir === "asc"
      ? " ▲"
      : " ▼";

    return (
      <button
        type="button"
        onClick={() => toggleSort(key)}
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          margin: 0,
          cursor: "pointer",
          fontSize: 10.5,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontWeight: 900,
          color: active ? "#111827" : "#64748b",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        {arrow}
      </button>
    );
  };

  const thStyle = (width) => ({ ...PRO_HEADER_CELL_STYLE, width });

  return (
    <div className="ft-page" style={{ padding: 12, gap: 10 }}>
      <div className="ft-pageHeader" style={{ marginBottom: 2, alignItems: "center" }}>
        <div>
          <div className="ft-h1" style={{ fontSize: "1.45rem", lineHeight: 1.08 }}>
            WIP Board
          </div>
          <div className="ft-subtle" style={{ marginTop: 4, fontSize: 11.5 }}>
            Live board for open repair orders, ownership, promise risk, and customer handoffs.
          </div>
        </div>

        <div style={{ marginLeft: "auto", fontSize: 12, opacity: 0.75 }}>
          Showing: <b>{sortedRows.length}</b> repair orders
          {isFiltered ? (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 900,
                color: "#b45309",
              }}
            >
              (Filtered)
            </span>
          ) : null}
        </div>
      </div>

      {tagParam ? (
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <button
            type="button"
            onClick={clearTagParam}
            title="Remove URL tag filter"
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #e5e7eb",
              background: "#fff",
              cursor: "pointer",
              fontWeight: 900,
              fontSize: 11.5,
            }}
          >
            Tag filter: {tagParam} ×
          </button>
        </div>
      ) : null}

      <div className="dispatch-kpiGrid">
        {[
          ["Open WIP", summary.totalVisible, "#111827"],
          ["Unassigned", summary.unassigned, "#111827"],
          ["Parts Review", summary.partsReview, "#9a3412"],
          ["Waiters", summary.waiters, "#111827"],
          ["In Progress", summary.inProgress, "#3730a3"],
          ["On Hold", summary.onHold, "#9a3412"],
          ["Vehicle Gone", summary.pickedUp, "#374151"],
          ["Overdue", summary.overdue, "#991b1b"],
        ].map(([label, value, color]) => (
          <div
            key={label}
            style={{
              border: "1px solid #e5e7eb",
              background:
                label === "In Progress"
                  ? "#eef2ff"
                  : label === "Parts Review"
                  ? "#fff7ed"
                  : "#ffffff",
              borderRadius: 8,
              padding: "10px 12px",
              boxShadow:
                label === "In Progress"
                  ? "0 0 0 1px rgba(67,56,202,0.08)"
                  : "none",
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 900, color: "#6b7280" }}>
              {label}
            </div>
            <div
              style={{
                marginTop: 3,
                fontSize: label === "In Progress" ? 20 : 18,
                fontWeight: 900,
                color,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {attentionItems.length ? (
        <div className="dispatch-attentionPanel">
          <div className="dispatch-attentionHeader">
            <div>
              <div className="dispatch-attentionEyebrow">Attention Needed</div>
              <div className="dispatch-attentionSub">
                Start here: highest-priority ROs before scanning the full board.
              </div>
            </div>
          </div>

          <div className="dispatch-attentionGrid">
            {attentionItems.map((item) => (
              <button
                key={`${item.label}-${item.ro?.id || item.ro?.roNumber}`}
                type="button"
                className={`dispatch-attentionItem dispatch-attentionItem--${item.tone}`}
                onClick={() => {
                  setSearch(safeString(item.ro?.roNumber || ""));
                  setOverdueOnly(false);
                  setStatusFilter("");
                }}
              >
                <div className="dispatch-attentionLabel">{item.label}</div>
                <div className="dispatch-attentionRO">
                  RO {safeString(item.ro?.roNumber || "—")}
                </div>
                <div className="dispatch-attentionMeta">
                  {safeString(item.ro?.vehicle || "—")} ·{" "}
                  {safeString(item.ro?.customerName || "—")}
                </div>
                <div className="dispatch-attentionDetail">{item.detail}</div>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div
        className="ft-card dispatch-proCard"
        style={{
          padding: 12,
          borderRadius: 4,
          overflow: "visible",
          border: "1px solid #dbe3ee",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isFiltered
              ? "minmax(320px, 1.9fr) minmax(170px, 1fr) minmax(220px, 1fr) auto"
              : "minmax(320px, 1.9fr) minmax(170px, 1fr) minmax(220px, 1fr)",
            gap: 8,
            alignItems: "center",
            marginBottom: 12,
            position: "sticky",
            top: 8,
            zIndex: 40,
            padding: 10,
            borderRadius: 10,
            background: "rgba(248,250,252,0.92)",
            backdropFilter: "blur(10px)",
            border: "1px solid #e5e7eb",
            boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
          }}
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search RO, tag, advisor, customer, vehicle, tech, flag, notes…"
            style={filterFieldStyle()}
          />

          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              height: 40,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#fff",
              fontSize: 12,
              fontWeight: 800,
              color: "#374151",
              cursor: "pointer",
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
            />
            Overdue only
          </label>

          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              height: 40,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#fff",
              fontSize: 12,
              fontWeight: 800,
              color: "#374151",
              cursor: "pointer",
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={includePickedUpVehicles}
              onChange={(e) => setIncludePickedUpVehicles(e.target.checked)}
            />
            Show Vehicle Gone
          </label>

          {isFiltered ? (
            <button
              type="button"
              onClick={clearAllFilters}
              style={{
                height: 32,
                padding: "0 10px",
                borderRadius: 7,
                border: "1px solid #e5e7eb",
                background: "#ffffff",
                color: "#6b7280",
                fontWeight: 700,
                fontSize: 11.5,
                cursor: "pointer",
                whiteSpace: "nowrap",
                width: "fit-content",
                justifySelf: "end",
              }}
            >
              Clear
            </button>
          ) : null}
        </div>

        <div
          ref={popoverRootRef}
          className="dispatch-proTableWrap"
          style={{
            overflowX: "auto",
            overflowY: "visible",
            position: "relative",
            zIndex: 1,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "#ffffff",
          }}
        >
          <table
            className="dispatch-proTable"
            style={{
              width: "100%",
              minWidth: 1180,
              borderCollapse: "collapse",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr>
                <th style={thStyle(COL.RO)}>{renderSortLabel(SORTS.RO_NUMBER, "RO")}</th>
                <th style={thStyle(COL.TAG)}>{renderSortLabel(SORTS.TAG, "Tag")}</th>
                <th style={thStyle(COL.ADVISOR)}>
                  <div style={{ display: "grid", gap: 3 }}>
                    {renderSortLabel(SORTS.ADVISOR, "Advisor")}
                    <select
                      value={advisorFilter}
                      onChange={(e) => setAdvisorFilter(e.target.value)}
                      style={compactHeaderSelectStyle()}
                    >
                      <option value="">All</option>
                      {advisorOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </th>
                <th style={thStyle(COL.VEHICLE)}>
                  {renderSortLabel(SORTS.VEHICLE, "Vehicle / Customer")}
                </th>
                <th style={thStyle(COL.TYPE)}>
                  <div style={{ display: "grid", gap: 3 }}>
                    {renderSortLabel(SORTS.TYPE, "Type")}
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      style={compactHeaderSelectStyle()}
                    >
                      <option value="">All</option>
                      {typeOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </th>
                <th style={thStyle(COL.DUE)}>{renderSortLabel(SORTS.DUE, "Promise")}</th>
                <th style={thStyle(COL.AGE)}>
                  <div style={{ display: "grid", gap: 3 }}>
                    {renderSortLabel(SORTS.AGE, "Age")}
                    <select
                      value={
                        sortBy === SORTS.AGE && sortDir === "asc"
                          ? "AGE_ASC"
                          : "AGE_DESC"
                      }
                      onChange={(e) => {
                        setSortBy(SORTS.AGE);
                        setSortDir(e.target.value === "AGE_ASC" ? "asc" : "desc");
                      }}
                      style={compactHeaderSelectStyle()}
                      title="Sort by age"
                    >
                      {AGE_SORT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </th>
                <th style={thStyle(COL.PRIORITY)}>
                  {renderSortLabel(SORTS.PRIORITY, "Priority")}
                </th>
                <th style={thStyle(COL.TECHNICIAN)}>
                  <div style={{ display: "grid", gap: 3 }}>
                    {renderSortLabel(SORTS.TECHNICIAN, "Technician")}
                    <select
                      value={technicianFilter}
                      onChange={(e) => setTechnicianFilter(e.target.value)}
                      style={compactHeaderSelectStyle()}
                    >
                      <option value="">All</option>
                      {technicianOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </th>
                <th style={thStyle(COL.STATUS)}>
                  <div style={{ display: "grid", gap: 3 }}>
                    {renderSortLabel(SORTS.STATUS, "Status")}
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={compactHeaderSelectStyle()}
                    >
                      <option value="">All</option>
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {statusDisplay(opt)}
                        </option>
                      ))}
                    </select>
                  </div>
                </th>
                <th style={thStyle(COL.FLAG)}>
                  <div style={{ display: "grid", gap: 3 }}>
                    {renderSortLabel(SORTS.FLAG, "Flag")}
                    <select
                      value={flagFilter}
                      onChange={(e) => setFlagFilter(e.target.value)}
                      style={compactHeaderSelectStyle()}
                    >
                      <option value="">All</option>
                      {flagOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </th>
                <th style={thStyle(COL.NOTES)}>Activity</th>
              </tr>
            </thead>

            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={12}
                    style={{
                      padding: 14,
                      border: "1px dashed #cbd5e1",
                      background: "#ffffff",
                      color: "#64748b",
                      fontSize: 12.5,
                    }}
                  >
                    No repair orders match this WIP view.
                  </td>
                </tr>
              ) : (
                sortedRows.map((ro) => {
                  const roId = ro?.id;
                  const dueISO = ro?.promiseTimeISO || ro?.promiseTime;
                  const dueMins = minutesUntil(dueISO);
                  const createdISO = roCreatedISO(ro);
                  const createdLabel = createdDateLabel(ro);
                  const currentAgeLabel = ageLabel(ro);
                  const currentAgeDays = ageDaysNumber(ro);

                  const primaryFlag = getPrimaryFlag(ro);
                  const flagTone = primaryFlag.tone;

                  const assignedTechId = safeString(ro?.assignedTechId).trim();
                  const assignedTechName =
                    safeString(ro?.assignedTechName).trim() || getTechName(assignedTechId);

                  const boardStatus = getBoardStatus(ro);
                  const isVehicleGone = boardStatus === "VEHICLE_GONE";
                  const isOverdue = !isVehicleGone && dueMins != null && dueMins < 0;
                  const overdue = isOverdue;
                  const dueSoon =
                    !isVehicleGone && dueMins != null && dueMins >= 0 && dueMins <= 60;

                  const latestShared = latestNote(ro?.notes);
                  const unreadNote = hasUnreadNote(ro);
                  const typeValue = safeUpper(ro?.transportType) || TRANSPORT_TYPES.OTHER;

                  const cellBase = {
                    padding: "4px 6px",
                    verticalAlign: "middle",
                    borderBottom: "1px solid #eef2f7",
                    position: "relative",
                    overflow: "visible",
                    ...rowHighlightStyle(flagTone, overdue, boardStatus),
                  };

                  const ellipsisText = {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  };

                  const typeEditorOpen = editor?.roId === roId && editor?.field === "type";
                  const techEditorOpen = editor?.roId === roId && editor?.field === "tech";
                  const statusEditorOpen = editor?.roId === roId && editor?.field === "status";

                  return (
                    <React.Fragment key={safeString(roId || ro?.roNumber || getTag(ro))}>
                      <tr
                        className="dispatch-proRow"
                        style={{
                          position: "relative",
                          zIndex: editor?.roId === roId ? 20 : 1,
                          cursor: "default",
                        }}
                      >
                        <td
                          style={{
                            ...cellBase,
                            borderLeft: overdue
                              ? "2px solid rgba(239,68,68,0.7)"
                              : boardStatus === "PARTS_NEEDED" || boardStatus === "PARTS_QUOTE_NEEDED"
                              ? "2px solid rgba(249,115,22,0.7)"
                              : "2px solid transparent",
                          }}
                        >
                          <b style={{ color: "#111827", fontSize: 12, ...ellipsisText }}>
                            {safeString(ro?.roNumber || "—")}
                          </b>
                        </td>

                        <td style={cellBase}>
                          {getTag(ro) ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "1px 5px",
                                borderRadius: 6,
                                fontSize: 10,
                                fontWeight: 800,
                                border: "1px solid #e5e7eb",
                                background: "#f9fafb",
                                color: "#0f172a",
                                maxWidth: "100%",
                                ...ellipsisText,
                              }}
                            >
                              {getTag(ro)}
                            </span>
                          ) : (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "1px 5px",
                                borderRadius: 6,
                                fontSize: 10,
                                fontWeight: 800,
                                border: "1px solid #fecaca",
                                background: "#fef2f2",
                                color: "#991b1b",
                              }}
                            >
                              Missing
                            </span>
                          )}
                        </td>

                        <td style={cellBase}>
                          <span
                            style={{
                              fontSize: 12,
                              color: "#111827",
                              lineHeight: 1.2,
                              display: "block",
                              ...ellipsisText,
                            }}
                            title={getAdvisorName(ro) || "—"}
                          >
                            {getAdvisorName(ro) || "—"}
                          </span>
                        </td>

                        <td style={cellBase}>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#111827",
                                lineHeight: 1.15,
                                ...ellipsisText,
                              }}
                              title={safeString(ro?.vehicle || "—")}
                            >
                              {safeString(ro?.vehicle || "—")}
                            </div>
                            <div
                              style={{
                                marginTop: 2,
                                fontSize: 10.5,
                                color: "#64748b",
                                lineHeight: 1.1,
                                ...ellipsisText,
                              }}
                              title={safeString(ro?.customerName || "—")}
                            >
                              {safeString(ro?.customerName || "—")}
                            </div>
                          </div>
                        </td>

                        <td style={cellBase}>
                          {canEditType ? (
                            <div style={{ position: "relative", zIndex: typeEditorOpen ? 1000 : 1 }}>
                              <button
                                type="button"
                                onClick={() => openEditor(roId, "type")}
                                style={commandFieldStyle(typeEditorOpen)}
                              >
                                {getTransportLabel(typeValue)}
                              </button>

                              {typeEditorOpen ? (
                                <div style={popMenuStyle(140)}>
                                  {[
                                    TRANSPORT_TYPES.WAITER,
                                    TRANSPORT_TYPES.LOANER,
                                    TRANSPORT_TYPES.SHUTTLE,
                                    TRANSPORT_TYPES.OTHER,
                                  ].map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => doChangeTransport(ro, opt)}
                                      style={popMenuItemStyle(opt === typeValue)}
                                    >
                                      {getTransportLabel(opt)}
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <span style={flagPillStyle("neutral")}>{getTransportLabel(typeValue)}</span>
                          )}
                        </td>

                        <td style={cellBase}>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#111827",
                                lineHeight: 1.1,
                                ...ellipsisText,
                              }}
                            >
                              {formatDueCompact(dueISO)}
                            </div>
                            {(overdue || dueSoon) && (
                              <div
                                style={{
                                  marginTop: 2,
                                  fontSize: 10.5,
                                  color: overdue ? "#b91c1c" : "#b45309",
                                  fontWeight: 600,
                                  lineHeight: 1.1,
                                  ...ellipsisText,
                                }}
                                title={formatRemaining(dueISO)}
                              >
                                {formatRemaining(dueISO)}
                              </div>
                            )}
                          </div>
                        </td>

                        <td style={cellBase}>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 900,
                                color:
                                  Number.isFinite(currentAgeDays) && currentAgeDays >= 3
                                    ? "#991b1b"
                                    : Number.isFinite(currentAgeDays) && currentAgeDays >= 1
                                    ? "#9a3412"
                                    : "#111827",
                                lineHeight: 1.1,
                                ...ellipsisText,
                              }}
                              title={`Created: ${createdLabel}`}
                            >
                              {currentAgeLabel}
                            </div>
                            <div
                              style={{
                                marginTop: 2,
                                fontSize: 10.5,
                                color: "#64748b",
                                lineHeight: 1.1,
                                ...ellipsisText,
                              }}
                              title={createdISO || ""}
                            >
                              Created {createdLabel}
                            </div>
                          </div>
                        </td>

                        <td style={cellBase}>
                          {(() => {
                            const signal = getPrioritySignal(ro);

                            if (!signal?.label || signal.label === "Normal") {
                              return <span style={{ fontSize: 11.5, color: "#94a3b8" }}>—</span>;
                            }

                            return (
                              <span style={flagPillStyle(signal.tone)} title={`Priority score: ${signal.score}`}>
                                {signal.label}
                              </span>
                            );
                          })()}
                        </td>

                        <td style={cellBase}>
                          {canAssignTech ? (
                            <div style={{ position: "relative", zIndex: techEditorOpen ? 1000 : 1 }}>
                              <button
                                type="button"
                                onClick={() => openEditor(roId, "tech")}
                                style={commandFieldStyle(techEditorOpen)}
                              >
                                {assignedTechName || "Unassigned"}
                              </button>

                              {techEditorOpen ? (
                                <div style={popMenuStyle(210)}>
                                  <button
                                    type="button"
                                    onClick={() => doAssignChange(ro, "")}
                                    style={popMenuItemStyle(!assignedTechId)}
                                  >
                                    Unassigned
                                  </button>
                                  {techOptions.map((opt) => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => doAssignChange(ro, opt.value)}
                                      style={popMenuItemStyle(String(assignedTechId) === String(opt.value))}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <span style={{ fontSize: 12, ...ellipsisText }}>
                              {assignedTechName || "—"}
                            </span>
                          )}
                        </td>

                        <td style={cellBase}>
                          {canEditStatus ? (
                            <div style={{ position: "relative", zIndex: statusEditorOpen ? 1000 : 1 }}>
                              <button
                                type="button"
                                onClick={() => openEditor(roId, "status")}
                                style={statusFieldStyle(boardStatus, statusEditorOpen)}
                              >
                                {statusDisplay(boardStatus)}
                              </button>

                              {statusEditorOpen ? (
                                <div style={popMenuStyle(190)}>
                                  {STATUS_OPTIONS.map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => doChangeStatus(ro, opt)}
                                      style={popMenuItemStyle(opt === boardStatus)}
                                    >
                                      {statusDisplay(opt)}
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <span style={statusPillStyle(boardStatus)}>{statusDisplay(boardStatus)}</span>
                          )}
                        </td>

                        <td style={cellBase}>
                          {canEditFlag ? (
                            <button
                              type="button"
                              onClick={() => setFlagModalROId(roId)}
                              style={{
                                ...commandFieldStyle(false),
                                height: 30,
                                fontWeight: 900,
                                color: primaryFlag.text ? "#111827" : "#94a3b8",
                                borderColor: primaryFlag.text ? "#fdba74" : "#d1d5db",
                                background: primaryFlag.text ? "#fff7ed" : "#ffffff",
                              }}
                              title={
                                safeString(ro?.manualFlag).trim()
                                  ? `Manual override: ${safeString(ro?.manualFlag).trim()}`
                                  : primaryFlag.text || "Set manual flag"
                              }
                            >
                              {primaryFlag.text || "—"}
                            </button>
                          ) : primaryFlag.text ? (
                            <span style={flagPillStyle(primaryFlag.tone)}>{primaryFlag.text}</span>
                          ) : (
                            <span style={{ fontSize: 11.5, color: "#94a3b8" }}>—</span>
                          )}
                        </td>

                        <td style={cellBase}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: unreadNote ? "auto 1fr" : "1fr",
                              gap: 5,
                              alignItems: "center",
                            }}
                          >
                            {unreadNote ? (
                              <span
                                title="Unread note"
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: 999,
                                  background: "#f59e0b",
                                }}
                              />
                            ) : null}
                            <span
                              style={{
                                fontSize: 10.5,
                                color: "#64748b",
                                lineHeight: 1.15,
                                ...ellipsisText,
                              }}
                              title={latestShared || "No notes"}
                            >
                              {latestShared || "No notes"}
                            </span>
                            <button
                              type="button"
                              onClick={() => openActivity(ro)}
                              style={{
                                gridColumn: "1 / -1",
                                justifySelf: "flex-start",
                                padding: "1px 0",
                                border: "none",
                                background: "transparent",
                                color: "#2563eb",
                                fontWeight: 800,
                                fontSize: 10.5,
                                cursor: "pointer",
                              }}
                            >
                              Activity
                            </button>
                          </div>
                        </td>
                      </tr>

                      {roId && flashByRO[roId] ? (
                        <tr>
                          <td
                            colSpan={12}
                            style={{
                              padding: "5px 8px",
                              background: "#f8fafc",
                              color: "#334155",
                              fontSize: 11,
                              fontWeight: 800,
                              borderBottom: "1px solid #e5e7eb",
                            }}
                          >
                            {flashByRO[roId]}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {flagModalRO ? (
        <div
          role="presentation"
          onClick={() => setFlagModalROId(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(15,23,42,0.32)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Set WIP flag"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 440,
              maxWidth: "100%",
              background: "#ffffff",
              border: "1px solid #dbe3ee",
              borderRadius: 16,
              boxShadow: "0 24px 70px rgba(15,23,42,0.24)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "14px 16px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 950,
                    color: "#111827",
                    lineHeight: 1.15,
                  }}
                >
                  Set WIP Flag
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: "#64748b",
                    lineHeight: 1.25,
                  }}
                >
                  RO #{safeString(flagModalRO?.roNumber || "—")} · {safeString(flagModalRO?.vehicle || "Vehicle")}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setFlagModalROId(null)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  background: "#ffffff",
                  cursor: "pointer",
                  fontWeight: 900,
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: 14, display: "grid", gap: 8 }}>
              {safeString(flagModalRO?.manualFlag).trim() ? (
                <div
                  style={{
                    padding: "9px 10px",
                    borderRadius: 10,
                    background: "#fff7ed",
                    border: "1px solid #fdba74",
                    color: "#9a3412",
                    fontSize: 12,
                    fontWeight: 850,
                  }}
                >
                  Current manual override: {safeString(flagModalRO?.manualFlag).trim()}
                </div>
              ) : getPrimaryFlag(flagModalRO).text ? (
                <div
                  style={{
                    padding: "9px 10px",
                    borderRadius: 10,
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    color: "#475569",
                    fontSize: 12,
                    fontWeight: 850,
                  }}
                >
                  Current automatic flag: {getPrimaryFlag(flagModalRO).text}
                </div>
              ) : null}

              <button
                type="button"
                onClick={() => doChangeFlag(flagModalRO, "", "")}
                style={{
                  ...popMenuItemStyle(false, true),
                  border: "1px solid #fee2e2",
                  background: "#fffafa",
                  padding: "10px 12px",
                }}
              >
                Clear Manual Override
              </button>

              {MANUAL_FLAG_OPTIONS.filter((opt) => opt.text).map((opt) => (
                <button
                  key={`${opt.label}-${opt.tone}`}
                  type="button"
                  onClick={() => doChangeFlag(flagModalRO, opt.text, opt.tone)}
                  style={{
                    ...popMenuItemStyle(opt.text === safeString(flagModalRO?.manualFlag).trim()),
                    border: "1px solid #e5e7eb",
                    background:
                      opt.text === safeString(flagModalRO?.manualFlag).trim()
                        ? "#f8fafc"
                        : "#ffffff",
                    padding: "10px 12px",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div
              style={{
                padding: "10px 16px 14px",
                color: "#64748b",
                fontSize: 11.5,
                lineHeight: 1.35,
              }}
            >
              Manual flags do not change Status. Status remains automatic from workflow actions.
            </div>
          </div>
        </div>
      ) : null}

      <ROActivityDrawer
        ro={activityRO}
        isOpen={!!activityRO}
        onClose={closeActivity}
        currentUser={currentUser}
        actorRole={actorRole}
        actorName={actorName}
        getTechName={getTechName}
        title="WIP RO Thread"
      />
    </div>
  );
}
